import { useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { Box, Button, Paragraph, TextButton } from '@zillow/constellation-10';
import { useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage } from '@zg-rentals/ts-satellite-client-react-query';
import rentalTourIllustration from '../../images/rental-tour.svg';
import EmptyState from '../EmptyState';
import StringConstants from '../../constants/StringConstants';
import { getContactsPageConversationsQueryParams } from '../../utils/contactsPageConversationsQueryParams';
import { getDateRangesInMsFromDaysBack } from '../../utils/getDateRangesInMsFromDaysBack';
import { useAuth } from '../../AuthProvider';
import {
  CLICK_ON_VIEW_RECENT_CONVERSATIONS_LINK_EVENT_TYPE_ID,
  emitClickOnViewRecentConversationsLinkEvent,
  emitDisplayViewRecentConversationsLinkEvent,
} from '../../constants/clickstreamEvents/leadManagementTable';

// Matches the Contacts page's "Last 90 days" option, which this link selects.
export const CONVERSATIONS_LOOKBACK_DAYS = 90;

type LeadManagementEmptyStateProps = {
  clientTimeZone: string;
  listingAliases: Array<string>;
  propertyDashboardOverviewUrl: string;
};

export default function LeadManagementEmptyState({
  clientTimeZone,
  listingAliases,
  propertyDashboardOverviewUrl,
}: LeadManagementEmptyStateProps) {
  const { user } = useAuth();

  // The contacts endpoint is single-listing, so the fallback only applies with exactly one listing selected.
  const listingAlias = listingAliases.length === 1 ? listingAliases[0] : '';

  const queryParams = useMemo(() => {
    const { startDateMs, endDateMs } = getDateRangesInMsFromDaysBack(CONVERSATIONS_LOOKBACK_DAYS);
    return getContactsPageConversationsQueryParams({
      clientTimeZone,
      startDateMs,
      endDateMs,
      limit: 1,
      start: 0,
      listingAlias,
    });
  }, [clientTimeZone, listingAlias]);

  const { data } = useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage(queryParams, {
    enabled: !!listingAlias,
    meta: {
      context: {
        api: 'getLatestConversationsForContactsPage',
        details: 'LeadManagementEmptyState - Error fetching conversations',
      },
    },
  });

  const hasConversations = !!listingAlias && (data?.conversations?.length ?? 0) > 0;

  // SLW has no auth boundary, so a cold load can render this before the user resolves - emitting
  // then would bake a null user into the impression. The ref dedupes within one mount, which only
  // spans a listing switch when both listings are cached (an uncached one unmounts this via
  // isLoadingLeadsData); clearing it whenever the link is absent keeps every reappearance a new view,
  // so impressions and the undeduped clicks stay comparable.
  const impressionAliasRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hasConversations) {
      impressionAliasRef.current = null;
      return;
    }
    if (!user || impressionAliasRef.current === listingAlias) return;
    impressionAliasRef.current = listingAlias;
    emitDisplayViewRecentConversationsLinkEvent(user, { listingAlias });
  }, [hasConversations, listingAlias, user]);

  return (
    <EmptyState
      illustration={rentalTourIllustration}
      heading={StringConstants.LEADS_EMPTY_STATE_HEADING}
      body={StringConstants.LEADS_EMPTY_STATE_BODY}
      bodyMaxWidth="357px"
      withCard
      css={{ marginTop: 'tighter' }}
      cta={
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'default' }}>
          <Button tone="brand" emphasis="filled" asChild>
            <a href={propertyDashboardOverviewUrl}>Review your listing</a>
          </Button>
          {hasConversations && (
            <Paragraph>
              {StringConstants.LEADS_EMPTY_STATE_CONVERSATIONS_HINT}{' '}
              <Link
                legacyBehavior
                href={{
                  pathname: '/leads/[[...listingAlias]]',
                  query: { listingAlias: [listingAlias], daysBack: String(CONVERSATIONS_LOOKBACK_DAYS) },
                }}
                passHref
              >
                <TextButton asChild onClick={() => emitClickOnViewRecentConversationsLinkEvent(user, { listingAlias })}>
                  <a data-eventid={CLICK_ON_VIEW_RECENT_CONVERSATIONS_LINK_EVENT_TYPE_ID}>
                    {StringConstants.LEADS_EMPTY_STATE_VIEW_CONVERSATIONS}
                  </a>
                </TextButton>
              </Link>
            </Paragraph>
          )}
        </Box>
      }
    />
  );
}
